import { useCallback, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { IBuiltInInstrumentSummary, IQuestionBankEntrySummary } from '@brushfire/types'
import {
  BuiltInInstrumentPreviewModal,
  BuiltInInstrumentRemoveDialog,
} from '@/features/built-in-instruments/components'
import { useBuiltInInstruments } from '@/features/built-in-instruments/api/built-in-instruments-queries'
import { useDeleteBuiltInInstrument } from '@/features/built-in-instruments/api/built-in-instruments-mutations'
import {
  InstrumentTemplateLibraryView,
  TemplatePreviewModal,
} from '@/features/instruments/templates/components'
import { useInstrumentTemplates } from '@/features/instruments/templates/api/template-queries'
import { useLibraryTemplateSearch } from '@/features/library/lib/libraryTemplateSearch'
import {
  QuestionBankCard,
  QuestionBankContentPanel,
  QuestionBankDetailDialog,
  QuestionBankPageSkeleton,
  QuestionBankRemoveDialog,
  questionBankGridSx,
} from '@/features/question-bank/components'
import {
  useQuestionBankEntries,
  useQuestionBankEntryDetail,
} from '@/features/question-bank/api/question-bank-queries'
import { useDeleteQuestionBankEntry } from '@/features/question-bank/api/question-bank-mutations'
import { SensemakerPageTitle } from '@/shared/components/SensemakerPageTitle'
import { ErrorState } from '@/shared/components/ErrorState'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useSessionStore } from '@/stores/useSessionStore'
import { LibrarySegmentTabs, type LibraryTab } from '@/features/library/components/LibrarySegmentTabs'

const LIBRARY_CONTENT_TOP_GAP_PX = 20

export function LibraryLandingPage() {
  useDocumentTitle('Library')
  const role = useSessionStore((s) => s.user?.role)
  const canMutate = role === 'ADMIN' || role === 'EDITOR'

  const [activeTab, setActiveTab] = useState<LibraryTab>('question-bank')
  const [detailEntryId, setDetailEntryId] = useState<string | null>(null)
  const [removeEntry, setRemoveEntry] = useState<IQuestionBankEntrySummary | null>(null)

  const { data: bankData, isLoading, isError, refetch } = useQuestionBankEntries()
  const deleteMutation = useDeleteQuestionBankEntry()
  const { data: detailEntry } = useQuestionBankEntryDetail(
    detailEntryId,
    Boolean(detailEntryId),
  )

  const entries = bankData?.entries ?? []

  const handleOpenDetail = useCallback((entry: IQuestionBankEntrySummary) => {
    setDetailEntryId(entry.id)
  }, [])

  const handleConfirmRemove = useCallback(async () => {
    if (!removeEntry) return
    const entryId = removeEntry.id
    try {
      await deleteMutation.mutateAsync(entryId)
      if (detailEntryId === entryId) {
        setDetailEntryId(null)
      }
      setRemoveEntry(null)
    } catch {
      // Keep dialog open on failure so the user can retry or cancel.
    }
  }, [deleteMutation, detailEntryId, removeEntry])

  return (
    <Box sx={{ position: 'relative' }} data-testid="library-landing-root">
      <SensemakerPageTitle
        title="Library"
        description="Reusable research assets. Save questions to your Question Bank or instruments to the Template Library."
      />

      <LibrarySegmentTabs value={activeTab} onChange={setActiveTab} />

      <Box sx={{ mt: `${LIBRARY_CONTENT_TOP_GAP_PX}px` }}>
        {activeTab === 'question-bank' ? (
          <QuestionBankContentPanel>
            {isLoading ? <QuestionBankPageSkeleton /> : null}
            {isError ? (
              <ErrorState
                onRetry={() => Promise.resolve(refetch()).catch(() => undefined)}
              />
            ) : null}
            {!isLoading && !isError && entries.length === 0 ? (
              <Typography sx={{ color: '#64748b', py: 4, textAlign: 'center' }}>
                No questions saved yet. Use the bookmark icon in the Question Builder to add questions
                to your bank.
              </Typography>
            ) : null}
            {!isLoading && !isError && entries.length > 0 ? (
              <Box data-testid="question-bank-grid" sx={questionBankGridSx}>
                {entries.map((entry) => (
                  <QuestionBankCard
                    key={entry.id}
                    entry={entry}
                    canRemove={canMutate}
                    onOpen={() => handleOpenDetail(entry)}
                    onRequestRemove={() => setRemoveEntry(entry)}
                  />
                ))}
              </Box>
            ) : null}
          </QuestionBankContentPanel>
        ) : (
          <LibraryTemplateTabContent canMutate={canMutate} />
        )}
      </Box>

      <QuestionBankDetailDialog
        open={Boolean(detailEntryId && detailEntry)}
        entry={detailEntry ?? null}
        onClose={() => setDetailEntryId(null)}
      />
      <QuestionBankRemoveDialog
        open={Boolean(removeEntry)}
        onCancel={() => setRemoveEntry(null)}
        onConfirm={() => void handleConfirmRemove()}
        isPending={deleteMutation.isPending}
      />
    </Box>
  )
}

function LibraryTemplateTabContent({ canMutate }: Readonly<{ canMutate: boolean }>) {
  const { search, setSearch } = useLibraryTemplateSearch()
  const { data: builtInData } = useBuiltInInstruments()
  const builtInEntries = builtInData?.entries ?? []
  const deleteBuiltInMutation = useDeleteBuiltInInstrument()

  const [previewTemplate, setPreviewTemplate] = useState<
    import('@brushfire/types').IInstrumentTemplateSummary | null
  >(null)
  const [previewBuiltIn, setPreviewBuiltIn] = useState<IBuiltInInstrumentSummary | null>(null)
  const [removeBuiltIn, setRemoveBuiltIn] = useState<IBuiltInInstrumentSummary | null>(null)

  const handleOpenTemplate = useCallback(
    (template: import('@brushfire/types').IInstrumentTemplateSummary) => {
      setPreviewTemplate(template)
    },
    [],
  )

  const handleConfirmRemoveBuiltIn = useCallback(async () => {
    if (!removeBuiltIn) return
    await deleteBuiltInMutation.mutateAsync(removeBuiltIn.id)
    setRemoveBuiltIn(null)
    if (previewBuiltIn?.id === removeBuiltIn.id) {
      setPreviewBuiltIn(null)
    }
  }, [deleteBuiltInMutation, previewBuiltIn, removeBuiltIn])

  return (
    <>
      <InstrumentTemplateLibraryView
        interactive
        onOpenTemplate={handleOpenTemplate}
        cardVariant="admin"
        showCardHeader={false}
        showInlineSearch={false}
        searchValue={search}
        onSearchChange={setSearch}
        builtInEntries={builtInEntries}
        canRemoveBuiltIn={canMutate}
        onOpenBuiltIn={setPreviewBuiltIn}
        onRequestRemoveBuiltIn={setRemoveBuiltIn}
      />
      <LibraryTemplatePreviewModal
        open={Boolean(previewTemplate)}
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
      />
      <BuiltInInstrumentPreviewModal
        open={Boolean(previewBuiltIn)}
        entry={previewBuiltIn}
        onClose={() => setPreviewBuiltIn(null)}
      />
      <BuiltInInstrumentRemoveDialog
        open={Boolean(removeBuiltIn)}
        onCancel={() => setRemoveBuiltIn(null)}
        onConfirm={() => void handleConfirmRemoveBuiltIn()}
        isPending={deleteBuiltInMutation.isPending}
      />
    </>
  )
}

function LibraryTemplatePreviewModal({
  open,
  template,
  onClose,
}: Readonly<{
  open: boolean
  template: import('@brushfire/types').IInstrumentTemplateSummary | null
  onClose: () => void
}>) {
  const { data: listData } = useInstrumentTemplates('')
  const categories = listData?.categories ?? []

  const previewCategoryLabel = useMemo(() => {
    if (!template) return ''
    const row = categories.find((c) => c.category === template.category)
    return row?.label ?? ''
  }, [categories, template])

  return (
    <TemplatePreviewModal
      open={open}
      template={template}
      categoryLabel={previewCategoryLabel}
      previewOnly
      onClose={onClose}
    />
  )
}
